// EnhancedLinkDialog.tsx
import React, { useState, useRef } from 'react';
import { clientStorageService, ClientFirebaseConfig } from '../client/clientStorageService';
import './EnhancedLinkDialog.css';

interface EnhancedLinkDialogProps {
  isOpen: boolean;
  initialUrl?: string;
  onInsert: (url: string) => void;
  onCancel: () => void;
  clientStorageConfig?: ClientFirebaseConfig; // Optional client storage config
}

const EnhancedLinkDialog: React.FC<EnhancedLinkDialogProps> = ({
  isOpen,
  initialUrl = '',
  onInsert,
  onCancel,
  clientStorageConfig
}) => {
  const [url, setUrl] = useState(initialUrl);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize client storage if config is provided
  React.useEffect(() => {
    if (clientStorageConfig && !clientStorageService.isReady()) {
      clientStorageService.initializeClientStorage(clientStorageConfig)
        .catch(error => {
          console.error('Failed to initialize client storage:', error);
          setUploadError('Failed to connect to file storage');
        });
    }
  }, [clientStorageConfig]);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Clear any previous errors
    setUploadError('');

    // Validate file type
    if (!clientStorageService.validateFileType(file)) {
      setUploadError('File type not supported. Please upload an image, document, or media file.');
      return;
    }

    // Check if client storage is ready
    if (!clientStorageService.isReady()) {
      setUploadError('File storage not available. Please contact support.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Upload the file with progress tracking
      const downloadURL = await clientStorageService.uploadFile(
        file,
        'editor-uploads',
        (progress) => {
          setUploadProgress(progress);
        }
      );

      // Set the URL in the input field
      setUrl(downloadURL);
      setUploadProgress(100);
      
      // Clear upload state after a short delay
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 1000);

    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
      setIsUploading(false);
      setUploadProgress(0);
    }

    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleInsert = () => {
    if (url.trim()) {
      onInsert(url.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleInsert();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (!isOpen) return null;

  const canUpload = clientStorageConfig && clientStorageService.isReady();

  return (
    <div className="link-dialog-overlay">
      <div className="link-dialog">
        <div className="link-dialog-header">
          <h3>Insert Link</h3>
          <button
            type="button"
            className="close-btn"
            onClick={onCancel}
            disabled={isUploading}
          >
            ✕
          </button>
        </div>

        <div className="link-dialog-body">
          <div className="url-input-section">
            <label htmlFor="url-input">URL or file link:</label>
            <div className="url-input-container">
              <input
                id="url-input"
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="https://example.com"
                disabled={isUploading}
                autoFocus
              />
              
              {canUpload && (
                <button
                  type="button"
                  className="file-upload-btn"
                  onClick={handleFileSelect}
                  disabled={isUploading}
                  title="Upload file"
                >
                  {isUploading ? '⏳' : '📁'}
                </button>
              )}
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
            accept="image/*,.pdf,.doc,.docx,.txt,.rtf,.mp4,.webm,.ogg,.mp3,.wav,.zip,.rar"
          />

          {/* Upload progress */}
          {isUploading && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span className="progress-text">
                Uploading... {Math.round(uploadProgress)}%
              </span>
            </div>
          )}

          {/* Upload error */}
          {uploadError && (
            <div className="upload-error">
              <span className="error-icon">⚠️</span>
              {uploadError}
            </div>
          )}

          {/* File upload info */}
          {canUpload && !isUploading && (
            <div className="upload-info">
              <small>
                📁 Click the folder icon to upload a file (max 10MB).
                <br />
                Supported: Images, PDFs, Documents, Media files
              </small>
            </div>
          )}

          {!canUpload && (
            <div className="upload-disabled-info">
              <small>
                ℹ️ File upload not available. Enter a URL manually.
              </small>
            </div>
          )}
        </div>

        <div className="link-dialog-footer">
          <button
            type="button"
            className="insert-btn"
            onClick={handleInsert}
            disabled={!url.trim() || isUploading}
          >
            Insert Link
          </button>
          <button
            type="button"
            className="cancel-btn"
            onClick={onCancel}
            disabled={isUploading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedLinkDialog;